import React, { useEffect, useState } from "react";
import api, { formatPrice } from "@/lib/api";
import { PageHead, Btn, Card, Select, Field } from "@/admin/ui";
import { toast } from "sonner";

const FULFIL = ["processing", "fulfilled", "cancelled"];
const DELIVERY = ["not_shipped", "shipped", "delivered"];

const OrdersAdmin = () => {
  const [orders, setOrders] = useState([]);
  const [sel, setSel] = useState(null);
  const [refundOpen, setRefundOpen] = useState(false);
  const [refundAmt, setRefundAmt] = useState("");
  const load = () => api.get("/admin/orders").then((r) => setOrders(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const remaining = sel ? Math.round((sel.total - (sel.refunded || 0)) * 100) / 100 : 0;
  const fullyRefunded = sel && sel.payment_status === "refunded";

  const save = async () => {
    try {
      const { data } = await api.put(`/admin/orders/${sel.id}`, { fulfilment_status: sel.fulfilment_status, delivery_status: sel.delivery_status, tracking: sel.tracking, notes: sel.notes });
      setSel(data); toast.success("Order updated"); load();
    } catch { toast.error("Failed"); }
  };
  const doRefund = async (partial) => {
    let amount = null;
    if (partial) {
      amount = Number(refundAmt);
      if (!amount || amount <= 0) { toast("Enter a valid amount."); return; }
      if (amount > remaining) { toast(`Max refundable is £${remaining.toFixed(2)}.`); return; }
    }
    try { const { data } = await api.post(`/admin/orders/${sel.id}/refund`, { amount }); setSel(data); setRefundOpen(false); setRefundAmt(""); toast.success("Refund issued"); load(); }
    catch (e) { toast.error(typeof e.response?.data?.detail === "string" ? e.response.data.detail : "Refund failed"); }
  };

  const badgeCls = (s) => s === "refunded" ? "bg-wine" : s === "partially_refunded" ? "bg-gold" : "bg-sage";

  if (sel) return (
    <div className="max-w-2xl">
      <PageHead title={sel.order_number} sub={`${sel.email} · ${sel.payment_status}`}>
        <Btn variant="ghost" onClick={() => setSel(null)}>Back</Btn>
        <Btn onClick={save} testid="admin-save">Save</Btn>
      </PageHead>
      <Card className="mb-4">
        {sel.items.map((i, x) => (
          <div key={x} className="flex justify-between text-sm py-1.5 border-b border-espresso/5">
            <span>{i.name} · {i.colour}/{i.size} · {i.sku} × {i.qty}</span><span>{formatPrice(i.price * i.qty)}</span>
          </div>
        ))}
        <div className="flex justify-between mt-3 font-medium"><span>Total</span><span>{formatPrice(sel.total)}</span></div>
        {sel.discount_amount > 0 && <p className="text-sage text-sm mt-1">Discount{sel.discount_code ? ` (${sel.discount_code})` : ""}: −{formatPrice(sel.discount_amount)}</p>}
        {sel.refunded > 0 && <p className="text-wine text-sm mt-1">Refunded: {formatPrice(sel.refunded)}</p>}
      </Card>
      <Card className="mb-4">
        <Select label="Fulfilment status" testid="admin-order-fulfilment" value={sel.fulfilment_status} onChange={(v) => setSel({ ...sel, fulfilment_status: v })} options={FULFIL} />
        <Select label="Delivery status" testid="admin-order-delivery" value={sel.delivery_status} onChange={(v) => setSel({ ...sel, delivery_status: v })} options={DELIVERY} />
        <Field label="Tracking number" testid="admin-order-tracking" value={sel.tracking} onChange={(v) => setSel({ ...sel, tracking: v })} />
        <Field label="Internal notes" testid="admin-order-notes" textarea value={sel.notes} onChange={(v) => setSel({ ...sel, notes: v })} />
      </Card>
      <Card>
        <p className="overline text-espresso/50 mb-3">Refunds</p>
        {fullyRefunded ? (
          <p className="text-sm text-wine">Fully refunded ({formatPrice(sel.refunded)}).</p>
        ) : (
          <>
            <p className="text-xs text-espresso/50 mb-3">Remaining refundable: {formatPrice(remaining)}</p>
            <div className="flex gap-3">
              <Btn variant="danger" testid="admin-order-refund-full" onClick={() => doRefund(false)}>Full Refund</Btn>
              <Btn variant="ghost" testid="admin-order-refund-partial" onClick={() => { setRefundAmt(""); setRefundOpen(true); }}>Partial Refund</Btn>
            </div>
          </>
        )}
      </Card>

      {refundOpen && (
        <div className="fixed inset-0 z-50 bg-espresso/40 flex items-center justify-center px-6" onClick={() => setRefundOpen(false)}>
          <div className="bg-cream p-6 w-full max-w-sm" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-serif text-2xl mb-1">Partial Refund</h3>
            <p className="text-xs text-espresso/50 mb-4">Max £{remaining.toFixed(2)}</p>
            <input type="number" step="0.01" min="0" max={remaining} value={refundAmt} onChange={(e) => setRefundAmt(e.target.value)}
              placeholder="Amount (£)" data-testid="admin-refund-amount" autoFocus
              className="w-full bg-ivory border border-espresso/20 px-3 py-2.5 text-sm outline-none focus:border-espresso mb-4" />
            <div className="flex gap-3 justify-end">
              <Btn variant="ghost" onClick={() => setRefundOpen(false)}>Cancel</Btn>
              <Btn variant="danger" testid="admin-refund-confirm" onClick={() => doRefund(true)}>Issue Refund</Btn>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  return (
    <div>
      <PageHead title="Orders" sub={`${orders.length} orders`} />
      <div className="bg-cream border border-espresso/10">
        {orders.length === 0 && <p className="p-8 text-espresso/40 text-sm">No orders yet. Complete a checkout to see orders here.</p>}
        {orders.map((o) => (
          <div key={o.id} onClick={() => setSel(o)} data-testid={`admin-order-row-${o.order_number}`}
            className="flex items-center gap-4 px-5 py-3 border-b border-espresso/5 hover:bg-sand/40 cursor-pointer">
            <div className="flex-1">
              <p className="font-medium">{o.order_number} <span className="text-xs text-espresso/40">{o.email}</span></p>
              <p className="text-xs text-espresso/50">{o.items.length} item(s) · {o.created_at?.slice(0, 10)}</p>
            </div>
            <span className={`text-[0.6rem] tracking-[0.14em] uppercase text-cream px-2.5 py-1 ${badgeCls(o.payment_status)}`}>{o.payment_status.replace("_", " ")}</span>
            <span className="text-sm">{formatPrice(o.total)}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

export default OrdersAdmin;
